import { Badge, Button, Group, Loader, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityDistrictRO } from '@routellers/types/dist/city-district/city-district.ro';
import { GuideLocaleStatusEnum } from '@routellers/types/dist/city-guide/enum/guide-locale-status.enum';
import {
    GUIDE_STATUS_ORDER,
    GuideStatusEnum,
} from '@routellers/types/dist/city-guide/enum/guide-status.enum';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { CityQuickSelect, parseCityFilter } from '../components/city-quick-select';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { DistrictEditor } from '../editors/district-editor';
import { enumOptions } from '../i18n';
import { LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';

const ALL = 'all';

const statusColor: Record<GuideStatusEnum, string> = {
    [GuideStatusEnum.DRAFT]: 'gray',
    [GuideStatusEnum.PUBLISHED]: 'green',
};

const displayName = (row: CityDistrictRO) =>
    row.locales?.find((locale) => locale.language === CONTENT_BASE_LANGUAGE && locale.name)?.name ||
    row.title;

export const DistrictsPage = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<CityDistrictRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [cityFilter, setCityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState(ALL);
    const [editorId, setEditorId] = useState<number | null>(null);
    const [creating, setCreating] = useState(false);
    const [backfilling, setBackfilling] = useState(false);

    const load = async () => {
        setLoading(true);
        const cityId = parseCityFilter(cityFilter);

        try {
            const where: Record<string, unknown> = {};

            if (cityId) where.cityId = cityId;
            if (statusFilter !== ALL) where.status = statusFilter;
            if (!Object.keys(where).length) where.id = null;

            const { data } = await api.cityDistrictsApi.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: search.trim() || undefined,
                where,
            });

            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [cityFilter, statusFilter]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityFilter, statusFilter, page]);

    const statusOptions = enumOptions(GUIDE_STATUS_ORDER, t, 'enums.guideStatus');

    const backfill = async () => {
        const cityId = parseCityFilter(cityFilter);

        if (
            !window.confirm(
                cityId
                    ? t('pages.districts.backfillConfirmCity')
                    : t('pages.districts.backfillConfirmAll'),
            )
        ) {
            return;
        }

        setBackfilling(true);

        try {
            const { data } = await api.cityDistrictsApi.backfill({
                data: cityId ? { cityId } : {},
            });

            notifications.show({
                color: 'green',
                message: t('pages.districts.backfillDone', {
                    created: data?.created ?? 0,
                    attractions: data?.linkedAttractions ?? 0,
                    establishments: data?.linkedEstablishments ?? 0,
                }),
            });
            load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBackfilling(false);
        }
    };

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.districts.title')}
                subtitle={t('pages.districts.subtitle')}
                action={
                    <Group gap='sm'>
                        <Button variant='default' loading={backfilling} onClick={backfill}>
                            {t('pages.districts.backfill')}
                        </Button>
                        <Button
                            onClick={() => {
                                setEditorId(null);
                                setCreating(true);
                            }}
                        >
                            {t('pages.districts.create')}
                        </Button>
                    </Group>
                }
            />

            <Group gap='sm' wrap='wrap'>
                <TextInput
                    placeholder={t('pages.districts.searchPlaceholder')}
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        if (page !== 1) setPage(1);
                        else load();
                    }}
                    w={260}
                />
                <CityQuickSelect
                    value={cityFilter}
                    onChange={(value) => {
                        setPage(1);
                        setCityFilter(value);
                    }}
                />
                <Select
                    w={180}
                    data={[{ value: ALL, label: t('pages.districts.statusAll') }, ...statusOptions]}
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value || ALL)}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={720}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.districts.columns.name')}</Table.Th>
                                    <Table.Th>{t('pages.districts.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.districts.columns.guide')}</Table.Th>
                                    <Table.Th>{t('pages.districts.columns.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row) => {
                                    const ready = (row.locales || []).filter(
                                        (locale) => locale.status === GuideLocaleStatusEnum.READY,
                                    );

                                    return (
                                        <Table.Tr
                                            key={row.id}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => {
                                                setCreating(false);
                                                setEditorId(row.id);
                                            }}
                                        >
                                            <Table.Td>
                                                <Text fw={550}>{displayName(row)}</Text>
                                                <Text size='xs' c='dimmed'>
                                                    {row.slug}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>{row.cityTitle || `#${row.cityId}`}</Table.Td>
                                            <Table.Td>
                                                {ready.length
                                                    ? ready
                                                          .map((locale) =>
                                                              locale.language.toUpperCase(),
                                                          )
                                                          .join(' ')
                                                    : '—'}
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge color={statusColor[row.status]} variant='light'>
                                                    {t(`enums.guideStatus.${row.status}`)}
                                                </Badge>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                                {!rows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={4}>
                                            <Text c='dimmed'>{t('pages.districts.empty')}</Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <ListPagination page={page} totalItems={total} onChange={setPage} />
                </>
            )}

            <DistrictEditor
                opened={creating || editorId != null}
                districtId={creating ? null : editorId}
                defaultCityId={parseCityFilter(cityFilter)}
                onClose={() => {
                    setCreating(false);
                    setEditorId(null);
                }}
                onSaved={load}
                onCreated={(id) => {
                    setCreating(false);
                    setEditorId(id);
                }}
                onDeleted={() => {
                    setCreating(false);
                    setEditorId(null);
                    load();
                }}
            />
        </Stack>
    );
};
